import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  MessageSquare,
  Bot,
  Users,
  GitBranch,
  Headphones,
  BarChart3,
  Building2,
  ArrowLeft,
  Check,
  Menu,
  X,
} from 'lucide-react';

const getTheme = () => localStorage.getItem('theme') || 'light';

// Reveals every [data-reveal] child inside the observed section once it
// enters the viewport, staggered by each child's data-reveal-index.
// Plain IntersectionObserver (no Motion/GSAP in this project) so the
// section.md "cheapest tool that works" rule stays satisfied.
const useRevealSection = () => {
  const ref = useRef(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    const items = node.querySelectorAll('[data-reveal]');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          items.forEach((el) => {
            const index = Number(el.dataset.revealIndex || 0);
            el.style.transitionDelay = `${Math.min(index * 60, 300)}ms`;
            el.classList.add('is-visible');
          });
          observer.disconnect();
        });
      },
      { threshold: 0.15 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return ref;
};

const CHANNELS = [
  { name: 'واتساب', slug: 'whatsapp', color: '#22c55e' },
  { name: 'ماسنجر', slug: 'messenger', color: '#0ea5e9' },
  { name: 'انستجرام', slug: 'instagram', color: '#f43f5e' },
  { name: 'تليجرام', slug: 'telegram', color: '#06b6d4' },
];

const FEATURES = [
  {
    icon: Bot,
    title: 'عميلك بياخد رد فوري… حتى وانت نايم',
    body: 'الذكاء الاصطناعي بيرد على الأسئلة المتكررة في ثانية، بالعربي الفصيح أو العامي، فمحدش بيستنى ومحدش بيزهق ويروح للمنافس.',
    span: 'md:col-span-2',
    tint: 'bg-white',
  },
  {
    icon: Users,
    title: 'اعرف عميلك قبل ما ترد',
    body: 'كل محادثاته وبياناته وتاريخه قدامك، فكل رد بيبقى شخصي وفي محله.',
    span: '',
    tint: 'bg-white',
  },
  {
    icon: Building2,
    title: 'كل محادثة توصل للموظف الصح',
    body: 'توزيع تلقائي على الأقسام بالتناوب أو حسب التخصص، فمحدش بيتأخر ومفيش رسالة بتضيع.',
    span: '',
    tint: 'card-accent',
  },
  {
    icon: GitBranch,
    title: 'وفّر ساعات من الشغل اليدوي',
    body: 'خلي المهام المتكررة تمشي لوحدها: غيّر الحالة، صعّد المحادثة، ابعت رسالة متابعة… كل ده تلقائي ومن غير ولا سطر كود.',
    span: 'md:col-span-2',
    tint: 'bg-white',
  },
  {
    icon: Headphones,
    title: 'حوّل زوار موقعك لعملاء',
    body: 'شات مباشر على موقعك بيبدأ المحادثة في اللحظة اللي العميل محتاجك فيها بالظبط.',
    span: '',
    tint: 'bg-white',
  },
  {
    icon: BarChart3,
    title: 'اعرف إيه اللي بيكسّبك',
    body: 'معدل الرد ورضا العملاء وأداء كل موظف قدامك، علشان تاخد قرارات مبنية على أرقام مش إحساس.',
    span: '',
    tint: 'bg-white',
  },
];

const PLANS = [
  { name: 'مجانية', price: '0', convos: '100 محادثة', cta: 'ابدأ مجانًا', featured: false },
  { name: 'أساسية', price: '299', convos: '1,000 محادثة', cta: 'ابدأ مجانًا', featured: false },
  { name: 'قياسية', price: '599', convos: '5,000 محادثة', cta: 'ابدأ مجانًا', featured: true },
  { name: 'مميزة', price: '999', convos: 'محادثات غير محدودة', cta: 'ابدأ مجانًا', featured: false },
];

const Landing = () => {
  const theme = getTheme();
  const isDark = theme === 'dark';
  const [menuOpen, setMenuOpen] = useState(false);
  const channelsRef = useRevealSection();
  const featuresRef = useRevealSection();
  const pricingRef = useRevealSection();
  const finalCtaRef = useRevealSection();

  useEffect(() => {
    document.title = 'هندل — ماتفوّتش ولا رسالة عميل تاني';
  }, []);

  return (
    <div className={`min-h-[100dvh] ${isDark ? 'bg-dark-900' : 'bg-white'}`}>
      {/* Nav */}
      <header className="sticky top-0 z-40 border-b border-slate-200 dark:border-dark-600 bg-white/90 dark:bg-dark-900/90 backdrop-blur-sm relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/welcome" className="inline-flex items-center gap-2.5 shrink-0">
            <div className="w-9 h-9 rounded-xl bg-ink flex items-center justify-center">
              <MessageSquare className="w-4.5 h-4.5 text-white" />
            </div>
            <span className="text-xl font-bold gradient-text">هندل</span>
          </Link>

          <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-slate-600 dark:text-slate-300">
            <a href="#features" className="hover:text-ink dark:hover:text-white transition-colors">المزايا</a>
            <a href="#channels" className="hover:text-ink dark:hover:text-white transition-colors">القنوات</a>
            <a href="#pricing" className="hover:text-ink dark:hover:text-white transition-colors">الأسعار</a>
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <Link to="/login" className="text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-ink dark:hover:text-white transition-colors px-3 py-2">
              تسجيل الدخول
            </Link>
            <Link to="/register" className="btn-primary text-sm py-2.5 px-4">
              ابدأ مجانًا
            </Link>
          </div>

          <button
            className="md:hidden p-2 text-ink dark:text-white"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="فتح القائمة"
          >
            {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        <div
          className="nav-dropdown md:hidden absolute inset-x-0 top-full border-t border-b border-slate-200 dark:border-dark-600 px-4 py-4 space-y-3 bg-white dark:bg-dark-900"
          data-closed={menuOpen ? undefined : ''}
          aria-hidden={!menuOpen}
        >
          <a href="#features" className="block text-sm font-medium text-slate-600 dark:text-slate-300 py-1" tabIndex={menuOpen ? 0 : -1}>المزايا</a>
          <a href="#channels" className="block text-sm font-medium text-slate-600 dark:text-slate-300 py-1" tabIndex={menuOpen ? 0 : -1}>القنوات</a>
          <a href="#pricing" className="block text-sm font-medium text-slate-600 dark:text-slate-300 py-1" tabIndex={menuOpen ? 0 : -1}>الأسعار</a>
          <div className="flex items-center gap-3 pt-2">
            <Link to="/login" className="btn-secondary text-sm py-2 px-4 flex-1 text-center" tabIndex={menuOpen ? 0 : -1}>تسجيل الدخول</Link>
            <Link to="/register" className="btn-primary text-sm py-2 px-4 flex-1 text-center" tabIndex={menuOpen ? 0 : -1}>ابدأ مجانًا</Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-16 md:pt-20 pb-16 md:pb-24">
        <div className="grid md:grid-cols-[1.1fr_0.9fr] gap-12 items-center">
          <div>
            <h1 className="hero-rise text-4xl md:text-6xl leading-[1.1] text-ink dark:text-white mb-5" style={{ animationDelay: '0ms' }}>
              ماتفوّتش ولا رسالة<br />عميل تاني
            </h1>
            <p className="hero-rise text-base md:text-lg text-slate-600 dark:text-slate-400 max-w-[46ch] leading-relaxed mb-8" style={{ animationDelay: '90ms' }}>
              عملاءك بيراسلوك على واتساب وانستجرام وماسنجر وتليجرام؟ هندل بيجمعهم في صندوق واحد ويرد على الأسئلة المتكررة تلقائيًا — علشان توفّر وقتك، تريّح فريقك، وتبيع أكتر.
            </p>
            <div className="hero-rise flex flex-wrap items-center gap-3" style={{ animationDelay: '170ms' }}>
              <Link to="/register" className="btn-primary py-3.5 px-6 text-base">
                ابدأ مجانًا
                <ArrowLeft className="w-4 h-4" />
              </Link>
              <Link to="/login" className="btn-secondary py-3.5 px-6 text-base">
                تسجيل الدخول
              </Link>
            </div>
            <p className="hero-rise text-sm text-slate-400 dark:text-slate-500 mt-5" style={{ animationDelay: '230ms' }}>
              تجربة مجانية 14 يوم، بدون بطاقة ائتمان
            </p>
          </div>

          {/* Unified-inbox visual: real channel identities converging into one thread */}
          <div className="hero-rise relative" style={{ animationDelay: '140ms' }}>
            <div className="grid grid-cols-2 gap-3">
              {CHANNELS.map((ch) => (
                <div
                  key={ch.slug}
                  className="card p-4 flex items-center gap-3"
                >
                  <div
                    className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
                    style={{ backgroundColor: `${ch.color}1a` }}
                  >
                    <img
                      src={`https://cdn.simpleicons.org/${ch.slug}/${ch.color.replace('#', '')}`}
                      alt={ch.name}
                      className="w-4.5 h-4.5"
                      width={18}
                      height={18}
                    />
                  </div>
                  <span className="text-sm font-medium text-ink dark:text-slate-100">{ch.name}</span>
                </div>
              ))}
            </div>

            <div className="artifact-card p-5 mt-3">
              <div className="flex items-center gap-2.5 mb-4">
                <div className="w-7 h-7 rounded-lg bg-ink flex items-center justify-center">
                  <MessageSquare className="w-3.5 h-3.5 text-white" />
                </div>
                <span className="text-sm font-bold text-ink dark:text-slate-100">صندوق وارد هندل</span>
                <span className="mr-auto text-[11px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
                  نشط
                </span>
              </div>
              <div className="space-y-2.5">
                {[
                  { name: 'محمد عبدالله', msg: 'عايز أعرف مواعيد التوصيل لمدينة نصر', ch: 'whatsapp' },
                  { name: 'سارة الشريف', msg: 'الطلب وصل امتى تقريبًا؟', ch: 'instagram' },
                  { name: 'يوسف كامل', msg: 'شكرًا على سرعة الرد 🙏', ch: 'telegram' },
                ].map((row) => (
                  <div key={row.name} className="flex items-center gap-3 py-2 border-t border-slate-100 dark:border-dark-700 first:border-t-0 first:pt-0">
                    <div className="w-8 h-8 rounded-full bg-mist dark:bg-dark-700 flex items-center justify-center text-xs font-bold text-slate-600 dark:text-slate-300 shrink-0">
                      {row.name[0]}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-ink dark:text-slate-100 truncate">{row.name}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{row.msg}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Customer outcomes strip */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-4">
        <div className="grid sm:grid-cols-3 gap-4">
          {[
            { k: 'ولا رسالة بتضيع', v: 'كل قنواتك في صندوق واحد، فمفيش عميل بيتنسى أو بيتأخر عليه الرد.' },
            { k: 'رد في ثواني', v: 'الذكاء الاصطناعي بيرد على الأسئلة المتكررة على مدار اليوم بدل ما عميلك يستنى.' },
            { k: 'فريقك منظم', v: 'كل محادثة بتتوزّع على الموظف الصح تلقائيًا، وانت شايف الأداء كله.' },
          ].map((item) => (
            <div key={item.k} className="card p-5">
              <p className="font-bold text-ink dark:text-slate-100 mb-1">{item.k}</p>
              <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">{item.v}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Channels */}
      <section id="channels" ref={channelsRef} className="max-w-7xl mx-auto px-4 sm:px-6 py-16 md:py-20 border-t border-slate-100 dark:border-dark-700">
        <div data-reveal data-reveal-index="0" className="reveal text-center mb-10">
          <h2 className="text-3xl md:text-4xl text-ink dark:text-white mb-3">عملاءك بيراسلوك من كل حتة. رد من مكان واحد.</h2>
          <p className="text-slate-500 dark:text-slate-400 max-w-[50ch] mx-auto">
            بطّل تقفل وتفتح تطبيقات. وصّل حساباتك مرة واحدة، وكل رسالة من أي قناة بتوصلك في نفس الصندوق.
          </p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {CHANNELS.map((ch, i) => (
            <div key={ch.slug} data-reveal data-reveal-index={i + 1} className="reveal card p-6 text-center">
              <div
                className="w-14 h-14 rounded-2xl mx-auto mb-4 flex items-center justify-center"
                style={{ backgroundColor: `${ch.color}1a` }}
              >
                <img
                  src={`https://cdn.simpleicons.org/${ch.slug}/${ch.color.replace('#', '')}`}
                  alt={ch.name}
                  className="w-7 h-7"
                  width={28}
                  height={28}
                />
              </div>
              <p className="font-bold text-ink dark:text-slate-100">{ch.name}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Features bento */}
      <section id="features" ref={featuresRef} className="max-w-7xl mx-auto px-4 sm:px-6 py-16 md:py-20 border-t border-slate-100 dark:border-dark-700">
        <div data-reveal data-reveal-index="0" className="reveal text-center mb-10">
          <h2 className="text-3xl md:text-4xl text-ink dark:text-white mb-3">كل اللي محتاجه علشان تخدم عملاءك صح</h2>
          <p className="text-slate-500 dark:text-slate-400 max-w-[50ch] mx-auto">
            من أول ما توصلك الرسالة لحد ما تعرف إيه اللي بيكسّبك — هندل معمول مخصوص لأصحاب البيزنس في مصر والمنطقة.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {FEATURES.map((f, i) => (
            <div key={f.title} data-reveal data-reveal-index={i + 1} className={`${f.tint} ${f.span} reveal card rounded-3xl p-6`}>
              <div className={`w-11 h-11 rounded-2xl flex items-center justify-center mb-4 ${f.tint === 'card-accent' ? 'bg-sienna/10' : 'bg-mist dark:bg-dark-700'}`}>
                <f.icon className={`w-5 h-5 ${f.tint === 'card-accent' ? 'text-sienna' : 'text-ink dark:text-slate-200'}`} />
              </div>
              <h3 className={`text-lg font-bold mb-1.5 ${f.tint === 'card-accent' ? 'text-sienna' : 'text-ink dark:text-slate-100'}`}>
                {f.title}
              </h3>
              <p className={`text-sm leading-relaxed ${f.tint === 'card-accent' ? 'text-sienna/80' : 'text-slate-500 dark:text-slate-400'}`}>
                {f.body}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" ref={pricingRef} className="max-w-7xl mx-auto px-4 sm:px-6 py-16 md:py-20 border-t border-slate-100 dark:border-dark-700">
        <div data-reveal data-reveal-index="0" className="reveal text-center mb-10">
          <h2 className="text-3xl md:text-4xl text-ink dark:text-white mb-3">أسعار بسيطة، تكبر معاك</h2>
          <p className="text-slate-500 dark:text-slate-400 max-w-[50ch] mx-auto">
            بالجنيه المصري، ودفع عن طريق فوري أو فودافون كاش.
          </p>
        </div>
        <div className="grid md:grid-cols-4 gap-4">
          {PLANS.map((plan, i) => (
            <div
              key={plan.name}
              data-reveal
              data-reveal-index={i + 1}
              className={`reveal price-card rounded-3xl p-6 flex flex-col ${
                plan.featured
                  ? 'bg-ink text-white'
                  : 'card'
              }`}
            >
              <p className={`text-sm font-bold mb-1 ${plan.featured ? 'text-slate-300' : 'text-slate-500 dark:text-slate-400'}`}>
                {plan.name}
              </p>
              <div className="flex items-baseline gap-1 mb-1">
                <span className={`text-3xl font-bold ${plan.featured ? 'text-white' : 'text-ink dark:text-slate-100'}`}>
                  {plan.price}
                </span>
                <span className={`text-sm ${plan.featured ? 'text-slate-300' : 'text-slate-400'}`}>
                  جنيه / شهر
                </span>
              </div>
              <p className={`text-sm mb-5 ${plan.featured ? 'text-slate-300' : 'text-slate-500 dark:text-slate-400'}`}>
                {plan.convos}
              </p>
              <Link
                to="/register"
                className={`text-center text-sm font-medium rounded-full py-2.5 mb-5 transition-colors duration-150 active:scale-[0.99] ${
                  plan.featured
                    ? 'bg-white text-ink hover:bg-slate-100'
                    : 'btn-secondary'
                }`}
              >
                {plan.cta}
              </Link>
              <ul className={`space-y-2 text-sm mt-auto ${plan.featured ? 'text-slate-200' : 'text-slate-600 dark:text-slate-300'}`}>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 shrink-0" /> كل القنوات
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 shrink-0" /> رد آلي بالذكاء الاصطناعي
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 shrink-0" /> تدفقات العمل
                </li>
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section ref={finalCtaRef} className="max-w-7xl mx-auto px-4 sm:px-6 pb-20">
        <div data-reveal data-reveal-index="0" className="reveal artifact-card p-10 md:p-14 text-center">
          <h2 className="text-3xl md:text-4xl text-ink dark:text-white mb-3">جرّب هندل مجانًا، وشوف الفرق بنفسك</h2>
          <p className="text-slate-500 dark:text-slate-400 max-w-[46ch] mx-auto mb-7">
            اربط قنواتك في دقايق وابدأ ترد على عملاءك أسرع النهاردة — ١٤ يوم مجانًا، بدون بطاقة ائتمان.
          </p>
          <Link to="/register" className="btn-primary py-3.5 px-7 text-base inline-flex">
            ابدأ مجانًا
            <ArrowLeft className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-100 dark:border-dark-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 flex flex-col md:flex-row items-center justify-between gap-5">
          <Link to="/welcome" className="inline-flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-ink flex items-center justify-center">
              <MessageSquare className="w-4 h-4 text-white" />
            </div>
            <span className="text-lg font-bold gradient-text">هندل</span>
          </Link>
          <nav className="flex items-center gap-6 text-sm text-slate-500 dark:text-slate-400">
            <a href="#features" className="hover:text-ink dark:hover:text-white transition-colors">المزايا</a>
            <a href="#pricing" className="hover:text-ink dark:hover:text-white transition-colors">الأسعار</a>
            <Link to="/login" className="hover:text-ink dark:hover:text-white transition-colors">تسجيل الدخول</Link>
          </nav>
          <p className="text-sm text-slate-400 dark:text-slate-500">© {new Date().getFullYear()} هندل</p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
